import { useState, FormEvent } from 'react';
import { Button, FormField, Select, Textarea } from './ui';
import { SupportService, SupportRequestData } from '../services/supportService';
import { ApiError } from '../errors/ApiError';

interface SupportFormData {
  type: 'Issue' | 'Request' | '';
  phone: string;
  functionArea: string;
  briefSummary: string;
  description: string;
}

interface SupportFormErrors {
  type?: string;
  functionArea?: string;
  description?: string;
  submit?: string;
}

interface SupportFormProps {
  onSuccess?: () => void;
}

const FUNCTION_AREA_OPTIONS = [
  'Shopping Views/Navigation',
  'Shopping Cart',
  'Checkout',
  'Promo/Discount',
  'Print/Business Cards',
  'Order Receipt/Status',
  'Reports',
  'Configuration',
  'Product/Inventory',
  'Approvals',
  'Other',
];

export function SupportForm({ onSuccess }: SupportFormProps) {
  const [formData, setFormData] = useState<SupportFormData>({
    type: '',
    phone: '',
    functionArea: '',
    briefSummary: '',
    description: '',
  });

  const [errors, setErrors] = useState<SupportFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: SupportFormErrors = {};

    if (!formData.type) {
      newErrors.type = 'Please select a request type';
    }

    if (!formData.functionArea) {
      newErrors.functionArea = 'Please select a function area';
    }

    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const requestData: SupportRequestData = {
        type: formData.type as 'Issue' | 'Request',
        phone: formData.phone || undefined,
        function_area: formData.functionArea,
        brief_summary: formData.briefSummary || undefined,
        description: formData.description,
      };

      await SupportService.submitSupportRequest(requestData);

      setSubmitSuccess(true);
      setFormData({
        type: '',
        phone: '',
        functionArea: '',
        briefSummary: '',
        description: '',
      });

      // Switch to contact tab after 2 seconds
      setTimeout(() => {
        onSuccess?.();
        setSubmitSuccess(false);
      }, 2000);
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 422 && error.errors) {
        // Map Laravel validation errors to form errors
        const validationErrors: SupportFormErrors = {};
        if (error.errors.type) validationErrors.type = error.errors.type[0];
        if (error.errors.function_area) validationErrors.functionArea = error.errors.function_area[0];
        if (error.errors.description) validationErrors.description = error.errors.description[0];
        setErrors(validationErrors);
      } else {
        setErrors({ submit: 'Failed to submit request. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBriefSummaryChange = (value: string) => {
    // Enforce max 25 characters
    if (value.length <= 25) {
      setFormData({ ...formData, briefSummary: value });
    }
  };

  if (submitSuccess) {
    return (
      <div className="text-center py-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Request Submitted Successfully
        </h3>
        <p className="text-gray-600">
          Thank you for contacting support. We will respond to your request shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Type" htmlFor="type" required error={errors.type}>
        <Select
          id="type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value as 'Issue' | 'Request' | '' })}
          error={!!errors.type}
          required
        >
          <option value="">Select type...</option>
          <option value="Issue">Issue</option>
          <option value="Request">Request</option>
        </Select>
      </FormField>

      <FormField label="Phone" htmlFor="phone">
        <input
          type="tel"
          id="phone"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          className="block w-full rounded-md border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:border-blue-500"
          placeholder="Your phone number"
        />
      </FormField>

      <FormField
        label="Function Area"
        htmlFor="functionArea"
        required
        error={errors.functionArea}
      >
        <Select
          id="functionArea"
          value={formData.functionArea}
          onChange={(e) => setFormData({ ...formData, functionArea: e.target.value })}
          error={!!errors.functionArea}
          required
        >
          <option value="">Select function area...</option>
          {FUNCTION_AREA_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      </FormField>

      <FormField
        label="Brief Summary"
        htmlFor="briefSummary"
        helperText={`${formData.briefSummary.length}/25 characters`}
      >
        <input
          type="text"
          id="briefSummary"
          value={formData.briefSummary}
          onChange={(e) => handleBriefSummaryChange(e.target.value)}
          maxLength={25}
          className="block w-full rounded-md border-gray-300 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:border-blue-500"
          placeholder="Brief one-line summary"
        />
      </FormField>

      <FormField
        label="Description"
        htmlFor="description"
        required
        error={errors.description}
        helperText={!errors.description ? 'Please provide a detailed description (minimum 10 characters)' : undefined}
      >
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          error={!!errors.description}
          required
          rows={6}
          placeholder="Describe the issue or request in detail..."
        />
      </FormField>

      {errors.submit && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          variant="primary"
          loading={isSubmitting}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Request'}
        </Button>
      </div>
    </form>
  );
}
